import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {exhaustMap, finalize, of} from 'rxjs';
import {catchError, map, mergeMap, switchMap, takeUntil} from 'rxjs/operators';
import * as ResearchPaperActions from './research-paper.actions';
import {ResearchPaperService} from './research-paper.service';
import {ResearchPaperDTO} from '../../dto/research-paper';
import {ScreeningService} from './screening.service';
import {
  startLoadingProcessInfoSseStream,
  stopLoadingProcessInfoSseStream
} from '../../../process-info/services/process-info.actions';
import {Store} from '@ngrx/store';
import {ScreeningProcessDTO} from '../../dto/screening-process';

@Injectable()
export class ResearchPaperEffects {
  private readonly paperService: ResearchPaperService = inject(ResearchPaperService);
  private readonly screeningService: ScreeningService = inject(ScreeningService);
  private readonly store: Store = inject(Store);
  private readonly actions$: Actions = inject(Actions);


  findResearchPapers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.startPaperResearch),
      exhaustMap((action) =>
        this.paperService.queryPapers(action.projectId).pipe(
          map((papers: ResearchPaperDTO[]) =>
            ResearchPaperActions.startPaperResearchComplete({projectId: action.projectId, papers})
          ),
          catchError((error) =>
            of(ResearchPaperActions.loadResearchPapersFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );


  loadResearchPapers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.loadResearchPapers),
      exhaustMap((action) =>
        this.paperService.listPapers(action.projectId).pipe(
          map((papers: ResearchPaperDTO[]) =>
            ResearchPaperActions.loadResearchPapersSuccess({projectId: action.projectId, papers})
          ),
          catchError((error) =>
            of(ResearchPaperActions.loadResearchPapersFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  createResearchPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.createResearchPaper),
      mergeMap((action) =>
        this.paperService.createPaper(action.projectId, action.paper, action.file).pipe(
          map((paper: ResearchPaperDTO) =>
            ResearchPaperActions.createResearchPaperSuccess({projectId: action.projectId, paper})
          ),
          catchError((error) =>
            of(ResearchPaperActions.createResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  createBulkResearchPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.createResearchBulkPaper),
      mergeMap((action) =>
        this.paperService.createBulkPaperCSV(action.projectId, action.csv, action.pdfs).pipe(
          map((papers: ResearchPaperDTO[]) =>
            ResearchPaperActions.createResearchBulkPaperSuccess({projectId: action.projectId, papers})
          ),
          catchError((error) =>
            of(ResearchPaperActions.createResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  updateScreeningResearchPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.updateScreeningResearchPaper),
      mergeMap((action) =>
        this.screeningService.overrideScreening(action.projectId, action.runId, action.screeningOne, action.screeningResult).pipe(
          map((paper: ResearchPaperDTO) =>
            ResearchPaperActions.updateResearchPaperSuccess({projectId: action.projectId, paper})
          ),
          catchError((error) =>
            of(ResearchPaperActions.updateResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  deleteScreeningResearchPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.deleteScreeningRun),
      mergeMap((action) =>
        this.screeningService.deleteScreeningRun(action.projectId, action.processId).pipe(
          map((paper: ResearchPaperDTO) =>
            ResearchPaperActions.updateResearchPaperSuccess({projectId: action.projectId, paper})
          ),
          catchError((error) =>
            of(ResearchPaperActions.updateResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );


  deleteResearchPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.deleteResearchPaper),
      mergeMap((action) =>
        this.paperService.deletePaper(action.projectId, action.id).pipe(
          map(() =>
            ResearchPaperActions.deleteResearchPaperSuccess({projectId: action.projectId, id: action.id})
          ),
          catchError((error) =>
            of(ResearchPaperActions.deleteResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  deleteAllResearchPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.deleteAllResearchPaper),
      mergeMap((action) =>
        this.paperService.deleteAllPaper(action.projectId).pipe(
          map(() =>
            ResearchPaperActions.deleteAllResearchPaperSuccess({projectId: action.projectId})
          ),
          catchError((error) =>
            of(ResearchPaperActions.deleteAllResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );
  retrieveResearchPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.retrieveResearchPaper),
      switchMap((action) =>
        this.paperService.retrievePaper(action.projectId, action.id).pipe(
          map((paper: ResearchPaperDTO) =>
            ResearchPaperActions.retrieveResearchPaperSuccess({projectId: action.projectId, paper})
          ),
          catchError((error) =>
            of(ResearchPaperActions.retrieveResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  updateResearchPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.updateResearchPaper),
      mergeMap((action) =>
        this.paperService.updatePaper(action.projectId, action.id, action.paper).pipe(
          map((paper: ResearchPaperDTO) =>
            ResearchPaperActions.updateResearchPaperSuccess({projectId: action.projectId, paper})
          ),
          catchError((error) =>
            of(ResearchPaperActions.updateResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  uploadResearchPaperPDF$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.uploadResearchPaperPDF),
      mergeMap((action) =>
        this.paperService.uploadPdf(action.projectId, action.id, action.file).pipe(
          map((paper: ResearchPaperDTO) =>
            ResearchPaperActions.updateResearchPaperSuccess({projectId: action.projectId, paper})
          ),
          catchError((error) =>
            of(ResearchPaperActions.updateResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  updateManuelScreeningResearchPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.updateManuelScreeningResearchPaper),
      mergeMap((action) =>
        this.paperService.updateManuelScreeningResearchPaper(action.projectId, action.processId, action.result).pipe(
          map((paper: ResearchPaperDTO) =>
            ResearchPaperActions.updateResearchPaperSuccess({projectId: action.projectId, paper})
          ),
          catchError((error) =>
            of(ResearchPaperActions.updateResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  createManuelScreeningResearchPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.createManuelScreeningResearchPaper),
      mergeMap((action) =>
        this.paperService.createManuelScreeningResearchPaper(action.projectId, action.paperId, action.result).pipe(
          map((process: ScreeningProcessDTO) =>
            ResearchPaperActions.createManuelScreeningResearchPaperSuccess({
              projectId: action.projectId,
              paperId: action.paperId,
              screening: process
            })
          ),
          catchError((error) =>
            of(ResearchPaperActions.updateResearchPaperFailure({projectId: action.projectId, error}))
          )
        )
      )
    )
  );

  startScreening$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.startPaperScreening),
      mergeMap((action) => {
        this.store.dispatch(startLoadingProcessInfoSseStream());
        return this.screeningService.startScreening(action.projectId, action.screeningRunSettings).pipe(
          map((paper) => ResearchPaperActions.updateResearchPaperSuccess({projectId: action.projectId, paper})),
          catchError((error) => of(ResearchPaperActions.paperScreeningFailure({projectId: action.projectId, error}))),
          takeUntil(this.actions$.pipe(ofType(ResearchPaperActions.startPaperScreeningComplete))),
          finalize(() => this.store.dispatch(stopLoadingProcessInfoSseStream()))
        );
      })
    )
  );

  startSseStream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.watchPaperScreening),
      mergeMap((action) => {
        this.store.dispatch(startLoadingProcessInfoSseStream());
        return this.screeningService.processScreeningStream(action.projectId).pipe(
          map((paper) => ResearchPaperActions.updateResearchPaperSuccess({projectId: action.projectId, paper})),
          catchError((error) => of(ResearchPaperActions.updateResearchPaperFailure({
            projectId: action.projectId,
            error
          }))),
          takeUntil(this.actions$.pipe(ofType(ResearchPaperActions.startPaperScreeningComplete))),
          finalize(() => this.store.dispatch(stopLoadingProcessInfoSseStream()))
        );
      })
    )
  );

  stopScreening$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.stopScreeningStart),
      switchMap(action =>
        this.screeningService.stopScreening(action.projectId).pipe(
          map(() => ResearchPaperActions.stopScreeningSuccess()),
          catchError(error => of(ResearchPaperActions.stopScreeningFailure({error: error.message})))
        )
      )
    )
  );

  startSseStreamOnePaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.startPaperScreeningOnePaper),
      mergeMap((action) => {
        this.store.dispatch(startLoadingProcessInfoSseStream());
        return this.screeningService.processScreeningStreamOnePaper(action.projectId, action.paperId).pipe(
          map((paper) => ResearchPaperActions.updateResearchPaperSuccess({projectId: action.projectId, paper})),
          catchError((error) => of(ResearchPaperActions.updateResearchPaperFailure({
            projectId: action.projectId,
            error
          }))),
          takeUntil(this.actions$.pipe(ofType(ResearchPaperActions.startPaperScreeningCompleteOnePaper))),
          finalize(() => this.store.dispatch(stopLoadingProcessInfoSseStream()))
        );
      })
    )
  );

  checkScreeningRunning$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResearchPaperActions.checkScreeningRunningStart),
      switchMap(action =>
        this.screeningService.isScreeningRunning(action.projectId).pipe(
          map(response => {
            if (response) {
              return ResearchPaperActions.watchPaperScreening({projectId: action.projectId});
            }
            return ResearchPaperActions.screeningNotRunning();
          }),
          catchError(error => of(ResearchPaperActions.screeningNotRunning()))
        )
      )
    )
  );
}
